import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  Container,
  Divider,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { usePointers } from "@/api/staticContent";
import {
  MANIFEST_EXAMPLES,
  MANIFEST_GROUPS,
  type IdSource,
  type VerbDoc,
} from "./manifestReference";
import { ID_LOOKUPS } from "./ManifestIdLookups";

const MANIFEST_DOC =
  "https://github.com/hats-and-ladders/hats-and-ladders/blob/main/packages/cli-util/qa-climber/MANIFEST.md";

interface SourceDoc {
  title: string;
  detail: string;
}

const SOURCES: Record<IdSource, SourceDoc> = {
  hatIndex: {
    title: "Hats",
    detail:
      "Hat ids for hat, set-top-hat, set-bottom-hat, like-perkquirk hat= and climb-plan hat=. Show hat options lists the ids a Climb Plan on that hat takes for goal=, best=, improve=, excited= and paths=. Those come from the plan's hat, not the district, and a wrong one fails the line with a list of every valid id.",
  },
  districtSettings: {
    title: "Programs and high schools in your district",
    detail:
      "program= and school= ids, from the district's published settings. Only active, published programs count (one added in the CMS since the last publish is rejected), and the program must also match the plan's hat. A school is offered only if it feeds from the climber's middle school, when any feeder is set.",
  },
  clusterPathways: {
    title: "Clusters and pathways",
    detail:
      "Cluster and pathway ids. Content lists the content block ids that cluster-content and pathway-content take.",
  },
  connectorActivities: {
    title: "Connector activities",
    detail: "activity= ids for building-block, and blocks= for plan-profile.",
  },
  perksQuirks: {
    title: "Perks and quirks",
    detail: "pq= ids for like-perkquirk.",
  },
  badges: {
    title: "Badges",
    detail: "badge= ids for like-perkquirk, already zero-padded.",
  },
  learning: {
    title: "Ladders, climbs and courses",
    detail: "Ids for ladder, climb and course.",
  },
  awards: {
    title: "Awards",
    detail: "Award ids for certificate.",
  },
  products: {
    title: "Products",
    detail: "Product codes for the product directive.",
  },
};

const SOURCE_ORDER: IdSource[] = [
  "hatIndex",
  "districtSettings",
  "clusterPathways",
  "connectorActivities",
  "perksQuirks",
  "badges",
  "learning",
  "awards",
  "products",
];

const anchor = (id: string) => `src-${id}`;

const Code = ({ children }: { children: string }) => (
  <Box
    component="pre"
    sx={{
      label: "manifest-code",
      m: 0,
      p: 1.5,
      bgcolor: "action.hover",
      borderRadius: 1,
      fontFamily: "monospace",
      fontSize: 13,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    }}
  >
    {children}
  </Box>
);

const VerbCard = ({ doc }: { doc: VerbDoc }) => (
  <Paper variant="outlined" sx={{ p: 2 }} id={`verb-${doc.verb}`}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Typography
        variant="subtitle1"
        sx={{ fontFamily: "monospace", fontWeight: 700 }}
      >
        {doc.verb}
      </Typography>
      <Chip
        label={doc.mode === "both" ? "ms + wf" : doc.mode}
        size="small"
        variant="outlined"
      />
    </Stack>
    <Typography
      variant="body2"
      sx={{ fontFamily: "monospace", color: "text.secondary", mb: 1 }}
    >
      {doc.shape}
    </Typography>
    <Typography variant="body2" sx={{ mb: 1 }}>
      {doc.summary}
    </Typography>

    {doc.args && (
      <Table size="small" sx={{ mb: 1 }}>
        <TableBody>
          {doc.args.map((arg) => (
            <TableRow key={arg.name}>
              <TableCell
                sx={{
                  fontFamily: "monospace",
                  whiteSpace: "nowrap",
                  verticalAlign: "top",
                  width: "1%",
                }}
              >
                {arg.name}
              </TableCell>
              <TableCell>{arg.detail}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )}

    {doc.notes?.map((note) => (
      <Typography
        key={note}
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1 }}
      >
        {note}
      </Typography>
    ))}

    <Stack spacing={1} sx={{ mb: doc.ids ? 1 : 0 }}>
      {doc.examples.map((example) => (
        <Code key={example}>{example}</Code>
      ))}
    </Stack>

    {doc.ids && (
      <Typography variant="body2" color="text.secondary">
        Look up ids:{" "}
        {doc.ids.map((id, i) => (
          <span key={id}>
            {i > 0 && ", "}
            <Link href={`#${anchor(id)}`}>{SOURCES[id].title}</Link>
          </span>
        ))}
      </Typography>
    )}
  </Paper>
);

export const QaManifestReferencePage = () => {
  const currentUser = useCurrentUser();
  const districtId: string | undefined = currentUser?.districtId;
  const { pointers, error: pointersError } = usePointers();

  return (
    <Container sx={{ py: 4 }} maxWidth="md">
      <Link component={RouterLink} to="/hldebug/provision-user">
        ← Provision QA user
      </Link>
      <Typography variant="h4" sx={{ mt: 1 }} gutterBottom>
        Manifest reference
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        One line is one action, applied top to bottom. # starts a comment, and
        blank lines are ignored. Tokens are separated by spaces; some are
        positional and some are key=value. Wrap a value in double quotes when it
        has spaces: description="two words".
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The full format is in{" "}
        <Link href={MANIFEST_DOC} target="_blank" rel="noreferrer">
          qa-climber/MANIFEST.md
        </Link>
        . Generate a complete file with{" "}
        <Box component="code" sx={{ fontFamily: "monospace" }}>
          pnpm qa-climber manifest generate
        </Box>{" "}
        and delete what you don't want, or check one without provisioning with{" "}
        <Box component="code" sx={{ fontFamily: "monospace" }}>
          pnpm qa-climber manifest validate --file &lt;f&gt;
        </Box>
        .
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Typography variant="subtitle2" gutterBottom>
          Contents
        </Typography>
        {MANIFEST_GROUPS.map((group) => (
          <Box key={group.id} sx={{ mb: 0.5 }}>
            <Link href={`#group-${group.id}`}>{group.title}</Link>
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ fontFamily: "monospace" }}
            >
              {"  "}
              {group.verbs.map((v) => v.verb).join(" · ")}
            </Typography>
          </Box>
        ))}
        <Box sx={{ mb: 0.5 }}>
          <Link href="#examples">Examples</Link>
        </Box>
        <Box>
          <Link href="#ids">Look up ids</Link>
        </Box>
      </Paper>

      <Stack spacing={5}>
        {MANIFEST_GROUPS.map((group) => (
          <Box key={group.id} id={`group-${group.id}`}>
            <Typography variant="h5" gutterBottom>
              {group.title}
            </Typography>
            {group.intro && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {group.intro}
              </Typography>
            )}
            <Stack spacing={2}>
              {group.verbs.map((doc) => (
                <VerbCard key={doc.verb} doc={doc} />
              ))}
            </Stack>
          </Box>
        ))}

        <Divider />

        <Box id="examples">
          <Typography variant="h5" gutterBottom>
            Examples
          </Typography>
          <Stack spacing={2}>
            {MANIFEST_EXAMPLES.map((example) => (
              <Paper
                key={example.id}
                variant="outlined"
                sx={{ p: 2 }}
                id={`example-${example.id}`}
              >
                <Typography variant="subtitle1">{example.title}</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {example.description}
                </Typography>
                <Code>{example.manifest}</Code>
              </Paper>
            ))}
          </Stack>
        </Box>

        <Divider />

        <Box id="ids">
          <Typography variant="h5" gutterBottom>
            Look up ids
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Type part of a name to get its id. These search the content
            published to this environment, which is what a manifest is checked
            against.
          </Typography>
          {pointersError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {pointersError.message}
            </Alert>
          )}

          <Stack spacing={2}>
            {SOURCE_ORDER.map((source) => {
              const Lookup = ID_LOOKUPS[source];
              return (
                <Paper
                  key={source}
                  variant="outlined"
                  sx={{ p: 2 }}
                  id={anchor(source)}
                >
                  <Typography variant="subtitle1">
                    {SOURCES[source].title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {SOURCES[source].detail}
                  </Typography>
                  {Lookup && pointers && (
                    <Lookup pointers={pointers} districtId={districtId} />
                  )}
                </Paper>
              );
            })}
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
};
